import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import {
  Typography,
  Tooltip,
  Fade,
  Grow,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Rating,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import HistoryIcon from "@mui/icons-material/History";
import StarIcon from "@mui/icons-material/Star";
import LanguageIcon from "@mui/icons-material/Language";
import CancelIcon from "@mui/icons-material/Cancel";
import { popupTimer, INPUT_LIMITS } from "../../consts";
import { useDispatch } from "react-redux";
import {
  getAllChats,
  getChatById,
  postAIRequest,
  updateSpaceRecommendations,
  deleteChatApi,
  renameChatApi,
  isRequestCancelled,
} from "./chatApi";
import { detectLanguage, makeMessageId } from "./chatUtils";
import MessageBubble from "./MessageBubble";
import SuggestionChips from "./SuggestionChips";
import ChatHistorySidebar from "./ChatHistorySidebar";
import useResearchPolling from "./useResearchPolling";

// Localized conversation starters shown on an empty chat.
const CONVERSATION_STARTERS = {
  english: [
    { icon: "📊", text: "Show me my recent data" },
    { icon: "✨", text: "Create a chart of my information" },
    { icon: "🔍", text: "What patterns do you see?" },
    { icon: "📁", text: "Create a collection for my items" },
  ],
  hebrew: [
    { icon: "📊", text: "הצג לי את הנתונים האחרונים שלי" },
    { icon: "✨", text: "צור תרשים מהמידע שלי" },
    { icon: "🔍", text: "אילו דפוסים אתה מזהה?" },
    { icon: "📁", text: "צור אוסף עבור הפריטים שלי" },
  ],
};

const EMPTY_STATE_TEXT = {
  english: {
    title: "How can I help you today?",
    subtitle: "Ask anything — analyze data, create collections, find patterns, or explore insights.",
  },
  hebrew: {
    title: "איך אפשר לעזור לך היום?",
    subtitle: "שאל כל דבר — נתח נתונים, צור אוספים, מצא דפוסים או גלה תובנות.",
  },
};

const GENERAL_SUGGESTIONS = {
  english: [
    "Tell me more about this",
    "Can you create a visualization?",
    "What patterns do you see?",
  ],
  hebrew: [
    "ספר לי עוד על זה",
    "אפשר ליצור מזה תרשים?",
    "אילו דפוסים אתה מזהה?",
  ],
};

const ChatComponent = ({ onResponse }) => {
  // --------------------------------------------------
  // States
  // --------------------------------------------------
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [userChats, setUserChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isCommunity, setIsCommunity] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messageHistoryIndex, setMessageHistoryIndex] = useState(-1);
  const [messageHistory, setMessageHistory] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [serverSuggestions, setServerSuggestions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingRecommendationSpaces, setPendingRecommendationSpaces] = useState(null);
  // Interactive space creation flow (render-only; the setter was dead code)
  const [interactiveSpaceCreation] = useState(null);
  const [recommendationPromptShown, setRecommendationPromptShown] = useState(false);
  const [researchTaskId, setResearchTaskId] = useState(null);
  const [researchQuery, setResearchQuery] = useState("");
  const [historicalWebsiteScrolls, setHistoricalWebsiteScrolls] = useState([]);
  const [showHistoricalWebsites, setShowHistoricalWebsites] = useState(false);
  const [isBrowsingMinimized, setIsBrowsingMinimized] = useState(false);

  // In-flight AI request, so it can be aborted on chat switch/close/unmount
  const abortControllerRef = useRef(null);
  const abortInFlightRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // Research task polling (status, discovered websites, cancel + cleanup)
  const {
    researchStatus,
    setResearchStatus,
    websiteScrolls,
    setWebsiteScrolls,
    cancelResearch,
  } = useResearchPolling({
    taskId: researchTaskId,
    isChatOpen,
    selectedChatId,
    loadChat,
    dispatch,
  });

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Listen for custom event to open chat (from mobile bottom nav)
  useEffect(() => {
    const handleOpenChat = () => {
      setIsChatOpen(true);
    };

    window.addEventListener("openChatDialog", handleOpenChat);

    return () => {
      window.removeEventListener("openChatDialog", handleOpenChat);
    };
  }, []);

  // Cleanup body padding and Toolbar padding when component unmounts or chat closes
  useEffect(() => {
    return () => {
      document.body.style.paddingRight = "";
      const toolbar = document.querySelector(".MuiAppBar-root .MuiToolbar-root");
      if (toolbar) {
        toolbar.style.paddingRight = "";
      }
    };
  }, []);

  useEffect(() => {
    if (!isChatOpen) {
      document.body.style.paddingRight = "";
      const toolbar = document.querySelector(".MuiAppBar-root .MuiToolbar-root");
      if (toolbar) {
        toolbar.style.paddingRight = "";
      }
    }
  }, [isChatOpen]);

  // Abort any in-flight AI request when the chat closes / component unmounts
  useEffect(() => {
    if (!isChatOpen) {
      abortInFlightRequest();
    }
  }, [isChatOpen]);

  useEffect(() => {
    return () => abortInFlightRequest();
  }, []);

  // Dispatch chat open/close state to Redux
  useEffect(() => {
    dispatch({ type: "SET_CHAT_OPEN", payload: isChatOpen });
  }, [isChatOpen, dispatch]);

  // Focus input when chat is opened
  useEffect(() => {
    if (isChatOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [isChatOpen]);

  // Focus input when AI finishes responding (isTyping goes from true to false)
  const prevIsTypingRef = useRef(isTyping);
  useEffect(() => {
    if (prevIsTypingRef.current === true && isTyping === false && isChatOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      prevIsTypingRef.current = isTyping;
      return () => clearTimeout(t);
    }
    prevIsTypingRef.current = isTyping;
  }, [isTyping, isChatOpen]);

  // Reset recommendation prompt flag when no pending spaces
  useEffect(() => {
    if (!pendingRecommendationSpaces) {
      setRecommendationPromptShown(false);
    }
  }, [pendingRecommendationSpaces]);

  // Auto-fullscreen when research mode is active
  useEffect(() => {
    if (researchTaskId && isChatOpen) {
      setIsFullscreen(true);
    }
  }, [researchTaskId, isChatOpen]);

  // Cleanup research state when chat closes
  useEffect(() => {
    if (!isChatOpen) {
      setResearchTaskId(null);
      setResearchQuery("");
      setIsBrowsingMinimized(false);
    }
  }, [isChatOpen]);

  // Reset minimized state when research task changes
  useEffect(() => {
    setIsBrowsingMinimized(false);
  }, [researchTaskId]);

  // Reset historical websites view when chat changes
  useEffect(() => {
    setShowHistoricalWebsites(false);
  }, [selectedChatId]);

  // Show recommendation prompt only after interactive flow is completed
  // This is a fallback in case the server doesn't return space_description mode
  useEffect(() => {
    if (!interactiveSpaceCreation && pendingRecommendationSpaces && !recommendationPromptShown) {
      // Check if the last message is a recommendation prompt already
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.user === "system") {
        const isRecommendationPrompt = lastMessage.isRecommendationPrompt;
        if (isRecommendationPrompt) {
          return;
        }
      }

      // Add a small delay to ensure the last message is rendered
      const timeoutId = setTimeout(() => {
        // Double-check conditions before showing
        if (!recommendationPromptShown && pendingRecommendationSpaces) {
          const lastUserMessage = messages.find((msg) => msg.user === "You");
          const userLanguage = lastUserMessage ? detectLanguage(lastUserMessage.text) : "english";

          const recommendationText = userLanguage === "hebrew"
            ? "🤖 האם תרצה שהמערכת תחפש ותציע לך באופן אוטומטי פריטים רלוונטיים שתוכל להוסיף לאוספים שנוצרו?\n\nזה יעזור לך למצוא תוכן מתאים לאוספים החדשים שלך."
            : "🤖 Would you like the system to automatically search and suggest relevant items that you can add to the created collections?\n\nThis will help you find suitable content for your new collections.";

          const recommendationPrompt = {
            id: makeMessageId(),
            user: "system",
            text: recommendationText,
            date: new Date().toLocaleString(),
            isRecommendationPrompt: true,
          };

          setMessages((prev) => [...prev, recommendationPrompt]);
          setRecommendationPromptShown(true);
        }
      }, 1500);

      return () => clearTimeout(timeoutId);
    }
  }, [interactiveSpaceCreation, pendingRecommendationSpaces, recommendationPromptShown, messages]);

  // --------------------------------------------------
  // Load the user's chats on mount
  // --------------------------------------------------
  useEffect(() => {
    setIsCommunity(window.location.href.includes("community"));

    // Restore chat state after navigation (if saved)
    try {
      const savedChatState = sessionStorage.getItem("aiChatState");
      if (savedChatState) {
        const chatState = JSON.parse(savedChatState);
        const timeDiff = Date.now() - chatState.timestamp;

        // Only restore if saved within last 10 seconds (fresh navigation)
        if (timeDiff < 10000) {
          // Open the chat
          setIsChatOpen(chatState.isChatOpen);

          // Restore selected chat
          if (chatState.selectedChatId) {
            setSelectedChatId(chatState.selectedChatId);
          }

          // Clear the saved state after restoring
          sessionStorage.removeItem("aiChatState");
        } else {
          // State too old, clear it
          sessionStorage.removeItem("aiChatState");
        }
      }
    } catch (error) {
      console.error("Error restoring chat state:", error);
      sessionStorage.removeItem("aiChatState");
    }
  }, []);

  useEffect(() => {
    if (selectedChatId) {
      loadChat(selectedChatId);
    }
  }, [selectedChatId]);

  useEffect(() => {
    if (isChatOpen) {
      loadUserChats();
    }
  }, [isChatOpen]);

  async function loadUserChats() {
    try {
      const data = await getAllChats();
      // data is expected to be { chats: [...] }
      if (data?.chats) {
        const transformed = data.chats.map((c) => ({
          id: c.id,
          name: c.name,
        }));
        setUserChats(transformed);
      }
    } catch (error) {
      console.error("Error loading chats:", error);
    }
  }

  // --------------------------------------------------
  // Load a single chat's data
  // --------------------------------------------------
  async function loadChat(chatId) {
    try {
      const data = await getChatById(chatId);
      // data might be { chats: [ { _id, name, chat_items: [...] } ] }
      const chat = data?.chats?.[0];
      if (!chat) return;

      const chatItems = chat.chat_items || [];

      // Collect all website_scrolls from all chat items and concatenate them
      const allWebsiteScrolls = [];
      chatItems.forEach((item) => {
        if (item.website_scrolls && Array.isArray(item.website_scrolls) && item.website_scrolls.length > 0) {
          allWebsiteScrolls.push(...item.website_scrolls);
        }
      });
      setHistoricalWebsiteScrolls(allWebsiteScrolls);

      const transformedMessages = chatItems.map((item, idx) => {
        const role = item.role === "system" ? "system" : "You";
        const textContent = item.content || item.summary || "";
        const timestamp = item.create_time || item.timestamp || new Date().toISOString();
        return {
          id: item.message_id || `${chatId}-${idx}`,
          user: role,
          text: textContent,
          html: item.html || null,
          date: timestamp,
        };
      });
      setMessages(transformedMessages);
    } catch (error) {
      console.error("Error loading chat:", error);
    }
  }

  function showErrorPopup(msg) {
    dispatch({
      type: "SET_POPUP",
      payload: {
        text: msg?.toString() || "Error occurred",
        type: "error",
      },
    });
    setTimeout(() => {
      dispatch({
        type: "SET_POPUP",
        payload: { text: "", type: "" },
      });
    }, popupTimer);
  }

  // --------------------------------------------------
  // Handle selecting a chat
  // --------------------------------------------------
  const handleSelectChat = (chatId) => {
    if (chatId !== selectedChatId) {
      // Cancel any in-flight request that belongs to the previous chat
      abortInFlightRequest();
      setIsTyping(false);
      setServerSuggestions([]);
    }
    setSelectedChatId(chatId);
  };

  // --------------------------------------------------
  // Create a new empty chat locally with a pseudo-ID
  // --------------------------------------------------
  const handleAddNewChat = () => {
    abortInFlightRequest();
    setIsTyping(false);
    setServerSuggestions([]);

    const tempId = `temp-${Date.now()}`;

    // Add the new chat to the user's chat list
    setUserChats((prevChats) => [
      ...prevChats,
      { id: tempId, name: "New Chat" },
    ]);

    // Select this new chat and clear the message history
    setSelectedChatId(tempId);
    setMessages([]);
  };

  // --------------------------------------------------
  // Delete / rename chats (UI lives in ChatHistorySidebar)
  // --------------------------------------------------
  const handleConfirmDelete = async (chatId) => {
    try {
      await deleteChatApi(chatId);
      setUserChats((prev) => prev.filter((c) => c.id !== chatId));
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setMessages([]);
      }
    } catch (err) {
      showErrorPopup("Failed to delete chat");
    }
  };

  const handleConfirmRename = async (chatId, newName) => {
    try {
      await renameChatApi(chatId, newName);
      setUserChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, name: newName } : c))
      );
    } catch (err) {
      showErrorPopup("Failed to rename chat");
    }
  };

  // --------------------------------------------------
  // Language detection for the UI (starters / suggestions)
  // --------------------------------------------------
  const isHebrewUser = useMemo(() => {
    const pool = [
      ...messages.slice(-8).map((m) => m.text || ""),
      ...messageHistory.slice(0, 5),
      ...userChats.map((c) => c.name || ""),
    ].join(" ");
    return /[֐-׿]/.test(pool);
  }, [messages, messageHistory, userChats]);

  const uiLanguage = isHebrewUser ? "hebrew" : "english";

  // --------------------------------------------------
  // Smart message suggestions (local fallback when the
  // server response has no `suggestions` field)
  // --------------------------------------------------
  const getSmartSuggestions = () => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.user === "You") return [];

    // Hebrew conversations get generic Hebrew follow-ups (the keyword
    // heuristics below only match English text)
    if (detectLanguage(lastMessage.text) === "hebrew") {
      return GENERAL_SUGGESTIONS.hebrew.slice(0, 3);
    }

    const suggestions = [];
    const messageText = (lastMessage.text || "").toLowerCase();

    // Context-aware suggestions
    if (messageText.includes("visualization") || messageText.includes("chart") || messageText.includes("graph")) {
      suggestions.push("Can you show me a different type of chart?");
      suggestions.push("What insights can you draw from this data?");
    }

    if (messageText.includes("price") || messageText.includes("cost")) {
      suggestions.push("Which items offer the best value?");
      suggestions.push("Show me the price trends");
    }

    if (messageText.includes("compare") || messageText.includes("comparison")) {
      suggestions.push("What are the key differences?");
      suggestions.push("Which option would you recommend?");
    }

    if (messageText.includes("space") || messageText.includes("collection") || messageText.includes("created") || messageText.includes("organize")) {
      suggestions.push("Create another collection for a different topic");
      suggestions.push("Add subspaces to organize better");
      suggestions.push("Show me my collections");
    }

    // General helpful suggestions
    if (suggestions.length === 0) {
      suggestions.push(...GENERAL_SUGGESTIONS.english);
    }

    return suggestions.slice(0, 3); // Limit to 3 suggestions
  };

  // Fallback heuristic chips: put the suggestion in the input for editing
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Server-provided suggestion chips: tapping sends the suggestion
  const handleServerSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setServerSuggestions([]);
    handleSendMessage(suggestion);
  };

  // Handle recommendation prompt response
  const handleRecommendationResponse = async (enableRecommendations) => {
    if (!pendingRecommendationSpaces) return;

    try {
      const spaceIds = pendingRecommendationSpaces.map((space) => space.id).filter((id) => id);

      if (spaceIds.length === 0) {
        console.warn("No valid space IDs found for recommendation update");
        return;
      }

      // Detect language from the last user message
      const lastUserMessage = messages.find((msg) => msg.user === "You");
      const userLanguage = lastUserMessage ? detectLanguage(lastUserMessage.text) : "english";

      if (enableRecommendations) {
        // First, ask if they want to guide the AI before enabling
        let guidancePromptText;
        if (userLanguage === "hebrew") {
          guidancePromptText = "🤖 האם יש לך משהו להכווין את ה-AI כדי שידע להביא המלצות טובות יותר?\n\nלדוגמה: 'חפש רק מוצרים במחיר עד 500 שקל' או 'תתמקד במתכונים טבעוניים פשוטים'\n\nאם כן - תכתוב מה תרצה להכווין, אם לא - רק תלחץ על 'לא, תודה'";
        } else {
          guidancePromptText = "🤖 Do you have any guidance for the AI to help it bring better recommendations?\n\nFor example: 'Search only for products under $100' or 'Focus on simple vegetarian recipes'\n\nIf yes - write your guidance, if not - just click 'No, thanks'";
        }

        const guidancePromptMsg = {
          id: makeMessageId(),
          user: "system",
          text: guidancePromptText,
          date: new Date().toLocaleString(),
          isGuidancePrompt: true,
          pendingRecommendationSpaces: pendingRecommendationSpaces,
        };

        setMessages((prev) => [...prev, guidancePromptMsg]);
        return; // Don't enable recommendations yet, wait for guidance response
      }

      // User said "no" to recommendations - clear pending
      setPendingRecommendationSpaces(null);

      const confirmationText = userLanguage === "hebrew"
        ? "👍 בסדר, לא הפעלתי המלצות אוטומטיות. תוכל תמיד להפעיל את זה מאוחר יותר בהגדרות האוסף."
        : "👍 Okay, I didn't enable automatic recommendations. You can always enable this later in the collection settings.";

      const confirmationMsg = {
        id: makeMessageId(),
        user: "system",
        text: confirmationText,
        date: new Date().toLocaleString(),
      };

      setMessages((prev) => [...prev, confirmationMsg]);
    } catch (error) {
      console.error("Error in recommendation response:", error);
    }
  };

  // Handle guidance response (after user responds to guidance prompt)
  const handleGuidanceResponse = async (hasGuidance, guidanceText = null) => {
    if (!pendingRecommendationSpaces) return;

    try {
      const spaceIds = pendingRecommendationSpaces.map((space) => space.id).filter((id) => id);

      if (spaceIds.length === 0) {
        console.warn("No valid space IDs found for recommendation update");
        return;
      }

      // Detect language from the last user message
      const lastUserMessage = messages.find((msg) => msg.user === "You");
      const userLanguage = lastUserMessage ? detectLanguage(lastUserMessage.text) : "english";

      // If user provided guidance, send it to AI first to save in background_context
      if (hasGuidance && guidanceText) {
        // Get current space ID
        let isSpacePage = window.location.href.includes("/spaces/");
        let spaceId = isSpacePage ? window.location.href.split("/").pop() : "";

        if (spaceId) {
          // Send the guidance text as an AI request to save it
          const aiPayload = {
            chat_id: selectedChatId || null,
            command: guidanceText,
            message_type: "space",
            space_id: spaceId,
            mode: "chat",
          };

          try {
            // Send to AI to detect and save guidance
            const aiResponse = await postAIRequest(aiPayload);

            // Add AI's response to messages
            if (aiResponse && aiResponse.summary) {
              const aiMsg = {
                id: makeMessageId(),
                user: "system",
                text: aiResponse.summary,
                date: new Date().toLocaleString(),
              };
              setMessages((prev) => [...prev, aiMsg]);
            }

            // Wait a bit for the update to complete
            await new Promise((resolve) => setTimeout(resolve, 300));
          } catch (error) {
            console.error("Error saving guidance:", error);
            // Continue anyway - will try to enable recommendations
          }
        }
      }

      // Now enable recommendations
      const response = await updateSpaceRecommendations(spaceIds, true);
      let confirmationText = "";

      // Check if there was partial success
      if (response.partial_success && response.total_skipped > 0) {
        // Partial success - some spaces were updated, some were skipped
        if (userLanguage === "hebrew") {
          let partialText = `✅ הפעלתי המלצות אוטומטיות עבור ${response.total_updated} אוספים`;
          if (response.child_spaces_count > 0) {
            partialText += ` (${response.main_spaces_count} ראשיים ו-${response.child_spaces_count} תת-אוספים)`;
          }
          partialText += `.\n\n⚠️ ${response.total_skipped} אוספים לא עודכנו בגלל מגבלת התוכנית`;
          if (response.child_spaces_skipped > 0) {
            partialText += ` (${response.main_spaces_skipped} ראשיים ו-${response.child_spaces_skipped} תת-אוספים)`;
          }
          partialText += `.\n\n💡 שדרג לתוכנית פרימיום כדי ליהנות מהמלצות ללא הגבלה!`;
          confirmationText = partialText;
        } else {
          let partialText = `✅ Enabled automatic recommendations for ${response.total_updated} collections`;
          if (response.child_spaces_count > 0) {
            partialText += ` (${response.main_spaces_count} main and ${response.child_spaces_count} sub-collections)`;
          }
          partialText += `.\n\n⚠️ ${response.total_skipped} collections were not updated due to plan limitations`;
          if (response.child_spaces_skipped > 0) {
            partialText += ` (${response.main_spaces_skipped} main and ${response.child_spaces_skipped} sub-collections)`;
          }
          partialText += `.\n\n💡 Upgrade to Premium plan to enjoy unlimited recommendations!`;
          confirmationText = partialText;
        }
      } else {
        // Full success
        if (userLanguage === "hebrew") {
          if (response.child_spaces_count > 0) {
            confirmationText = `✅ נהדר! הפעלתי המלצות אוטומטיות עבור ${response.main_spaces_count} אוספים ראשיים ו-${response.child_spaces_count} תת-אוספים (סה"כ ${response.total_updated} אוספים). המערכת תחפש ותציע לך פריטים רלוונטיים שתוכל להוסיף לכל האוספים.`;
          } else {
            confirmationText = `✅ נהדר! הפעלתי המלצות אוטומטיות עבור ${response.total_updated} אוספים. המערכת תחפש ותציע לך פריטים רלוונטיים שתוכל להוסיף לאוספים.`;
          }
        } else {
          if (response.child_spaces_count > 0) {
            confirmationText = `✅ Great! Enabled automatic recommendations for ${response.main_spaces_count} main collections and ${response.child_spaces_count} sub-collections (total ${response.total_updated} collections). The system will search and suggest relevant items you can add to all collections.`;
          } else {
            confirmationText = `✅ Great! Enabled automatic recommendations for ${response.total_updated} collections. The system will search and suggest relevant items you can add to the collections.`;
          }
        }
      }

      const confirmationMsg = {
        id: makeMessageId(),
        user: "system",
        text: confirmationText,
        date: new Date().toLocaleString(),
      };

      setMessages((prev) => [...prev, confirmationMsg]);

      const successMessage = userLanguage === "hebrew"
        ? "המלצות אוטומטיות הופעלו בהצלחה!"
        : "Automatic recommendations enabled successfully!";

      dispatch({
        type: "SET_POPUP",
        payload: {
          text: successMessage,
          type: "success",
        },
      });
      setTimeout(() => {
        dispatch({
          type: "SET_POPUP",
          payload: { text: "", type: "" },
        });
      }, 3000);

      // Clear pending after successful enablement
      setPendingRecommendationSpaces(null);
    } catch (error) {
      console.error("Error updating space recommendations:", error);

      // Detect language from the last user message
      const lastUserMessage = messages.find((msg) => msg.user === "You");
      const userLanguage = lastUserMessage ? detectLanguage(lastUserMessage.text) : "english";

      let errorText;
      let errorPopupText;

      if (userLanguage === "hebrew") {
        errorText = "מצטער, הייתה בעיה בעדכון ההגדרות. אנא נסה שוב מאוחר יותר.";
        errorPopupText = "שגיאה בעדכון הגדרות ההמלצות";

        // Check for specific error messages
        if (error.response?.data?.error) {
          const serverError = error.response.data.error;
          if (serverError.includes("Cannot enable recommendations for more than")) {
            errorText = `⚠️ ${serverError}\n\nכדי להפעיל המלצות לאוספים החדשים, תצטרך לבטל המלצות מאוספים אחרים או לשדרג לתוכנית פרימיום.`;
          } else if (serverError.includes("exceeding the limit")) {
            errorText = "⚠️ המלצות אוטומטיות זמינות רק ל-5 אוספים בתוכנית החינמית. שדרג לתוכנית פרימיום כדי ליהנות מהמלצות ללא הגבלה.";
          }
        }
      } else {
        errorText = "Sorry, there was a problem updating the settings. Please try again later.";
        errorPopupText = "Error updating recommendation settings";

        // Check for specific error messages
        if (error.response?.data?.error) {
          const serverError = error.response.data.error;
          if (serverError.includes("Cannot enable recommendations for more than")) {
            errorText = `⚠️ ${serverError}\n\nTo enable recommendations for new collections, you'll need to disable recommendations from other collections or upgrade to Premium plan.`;
          } else if (serverError.includes("exceeding the limit")) {
            errorText = "⚠️ Automatic recommendations are only available for 5 collections in the free plan. Upgrade to Premium plan to enjoy unlimited recommendations.";
          }
        }
      }

      const errorMsg = {
        id: makeMessageId(),
        user: "system",
        text: errorText,
        date: new Date().toLocaleString(),
      };

      setMessages((prev) => [...prev, errorMsg]);

      showErrorPopup(errorPopupText);
    } finally {
      setPendingRecommendationSpaces(null);
    }
  };

  // --------------------------------------------------
  // Handle sending a message
  // `overrideText` lets suggestion chips / retry send directly,
  // bypassing the input field state.
  // --------------------------------------------------
  const handleSendMessage = async (overrideText) => {
    // 1. Basic validation
    const messageText = (typeof overrideText === "string" ? overrideText : inputValue).trim();
    if (!messageText || isTyping) return;

    // 2. Add to message history for ↑↓ navigation
    setMessageHistory((prev) => {
      const newHistory = [messageText, ...prev.filter((msg) => msg !== messageText)];
      return newHistory.slice(0, 10);
    });
    setMessageHistoryIndex(-1);

    // 3. Add user's message to UI
    const userMsg = {
      id: makeMessageId(),
      user: "You",
      text: messageText,
      date: new Date().toLocaleString(),
    };
    const userLanguage = detectLanguage(userMsg.text);
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);
    setShowSuggestions(false);
    setServerSuggestions([]);
    setTimeout(() => inputRef.current?.focus(), 0);

    // Cancellable request: aborted on chat switch/close/unmount
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // 4. Determine context
      let chatIdToSend = selectedChatId || null;
      const isSpacePage = window.location.href.includes("/spaces/");
      const resolvedSpaceId = isSpacePage ? window.location.href.split("/").pop() : "";
      let messageType = "general";
      let spaceIdForPayload = null;

      if (isSpacePage && resolvedSpaceId) {
        messageType = "space";
        spaceIdForPayload = resolvedSpaceId;
      } else if (isCommunity) {
        messageType = "community";
      }

      // 5. Build request payload - simple and clean
      const payload = {
        chat_id: chatIdToSend,
        command: messageText,
        message_type: messageType,
        space_id: spaceIdForPayload,
        mode: "chat",
      };

      // 6. Call AI backend - it handles everything
      const resp = await postAIRequest(payload, { signal: controller.signal });
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setIsTyping(false);

      // 7. Validate response
      if (!resp) {
        console.error("No response from server");
        const errorMsg = {
          id: makeMessageId(),
          user: "system",
          text: "I'm sorry, I didn't receive a response from the server. Please try again.",
          date: new Date().toLocaleString(),
          isError: true,
          failedCommand: messageText,
        };
        setMessages((prev) => [...prev, errorMsg]);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }

      // 8. Check for research task (new async research system)
      if (resp.task_id && resp.status === "research_started") {
        const taskId = resp.task_id;
        const query = resp.actions?.data?.query || resp.explore_query || "";

        setResearchTaskId(taskId);
        setResearchQuery(query);
        setResearchStatus("pending");
        setWebsiteScrolls([]);
      } else if (resp.actions?.type === "research_started" && resp.actions?.data?.task_id) {
        // Fallback: check actions
        const taskId = resp.actions.data.task_id;
        const query = resp.actions.data.query || "";

        setResearchTaskId(taskId);
        setResearchQuery(query);
        setResearchStatus("pending");
        setWebsiteScrolls([]);
      } else {
        // Clear research state if not a research request
        if (!resp.task_id) {
          setResearchTaskId(null);
          setResearchQuery("");
          setResearchStatus(null);
          setWebsiteScrolls([]);
        }
      }

      // 9. Update chat if new
      const newChatId = resp?.chat_id;
      const chatName = resp?.name || "New Chat";
      if ((!selectedChatId || selectedChatId.startsWith("temp")) && newChatId) {
        setSelectedChatId(newChatId);
        setUserChats((prev) => [...prev, { id: newChatId, name: chatName }]);
      }

      // 10. Display AI message (from backend - no hardcoded messages!)
      // Handle split messages for better readability
      const aiResponse = resp.summary || resp.message || "";
      const additionalMessages = resp.additional_messages || [];

      // Check if recommendations were enabled/disabled (clear flag from backend)
      if (resp.recommendations_enabled !== undefined) {
        const spaceId = resp.space_id_for_recommendations;
        const enabled = resp.recommendations_enabled;

        if (enabled && spaceId) {
          const enabledText = userLanguage === "hebrew"
            ? "✅ ההמלצות הופעלו בהצלחה! המערכת תחפש ותציע פריטים רלוונטיים שתוכל להוסיף לאוסף הזה."
            : "✅ Recommendations enabled successfully! The system will now automatically search and suggest relevant items for this collection.";

          const popupText = userLanguage === "hebrew"
            ? "ההמלצות הופעלו בהצלחה!"
            : "Recommendations enabled successfully!";

          // Show success message for enabling recommendations
          const successMsg = {
            id: makeMessageId(),
            user: "system",
            text: enabledText,
            date: new Date().toLocaleString(),
          };
          setMessages((prev) => [...prev, successMsg]);

          // Refresh spaces to update the UI
          dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });

          // Show popup notification
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: popupText,
              type: "success",
            },
          });
          setTimeout(() => {
            dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
          }, 3000);
        } else if (!enabled && spaceId) {
          const disabledText = userLanguage === "hebrew"
            ? "ההמלצות בוטלו בהצלחה."
            : "Recommendations disabled successfully.";

          // Show message for disabling recommendations
          const successMsg = {
            id: makeMessageId(),
            user: "system",
            text: disabledText,
            date: new Date().toLocaleString(),
          };
          setMessages((prev) => [...prev, successMsg]);

          // Refresh spaces to update the UI
          dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });
        }
      }

      if (aiResponse.trim()) {
        const systemMsg = {
          id: makeMessageId(),
          user: "system",
          text: aiResponse,
          html: resp.html || null,
          date: new Date().toLocaleString(),
        };
        setMessages((prev) => [...prev, systemMsg]);

        // Add additional message chunks with a small delay for better flow
        if (additionalMessages.length > 0) {
          additionalMessages.forEach((chunk, index) => {
            setTimeout(() => {
              const additionalMsg = {
                id: makeMessageId(),
                user: "system",
                text: chunk,
                html: null, // Only first message has HTML
                date: new Date().toLocaleString(),
              };
              setMessages((prev) => [...prev, additionalMsg]);
            }, (index + 1) * 300); // 300ms delay between chunks
          });
        }
      }

      // 11. Execute actions from backend (if any)
      const actions = resp.actions;
      if (actions) {
        switch (actions.type) {
          case "navigate_to_space": {
            // Navigate to newly created space
            const spaceId = actions.data?.space_id;
            const spaceName = actions.data?.space_name;

            if (spaceId) {
              // First refresh spaces to update sidebar
              dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });

              // Show success message
              const successMessage = spaceName
                ? `Space "${spaceName}" created successfully! Opening...`
                : "Space created successfully! Opening...";

              dispatch({
                type: "SET_POPUP",
                payload: { text: successMessage, type: "success" },
              });

              // Save chat state before navigation so it persists after page reload
              const chatState = {
                isChatOpen: true,
                selectedChatId: selectedChatId || newChatId,
                chatName: chatName,
                timestamp: Date.now(),
              };
              sessionStorage.setItem("aiChatState", JSON.stringify(chatState));

              // Navigate to the new space after a short delay
              setTimeout(() => {
                window.location.href = `/spaces/${spaceId}`;
              }, 800);

              // Clear popup after navigation
              setTimeout(() => {
                dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
              }, 3000);
            }
            break;
          }

          case "reload_space": {
            // Space was updated - reload sidebar and current space if needed
            const updatedSpaceId = actions.data?.space_id;
            const updates = actions.data?.updates || {};

            // Refresh spaces in sidebar
            dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });

            // Check if we're currently in the updated space
            let currentSpaceId = "";
            if (window.location.href.includes("/spaces/")) {
              currentSpaceId = window.location.href.split("/").pop();
            }

            // Build success message based on what was updated
            // Note: Recommendations updates are handled separately via recommendations_enabled flag
            let updateMessage = "Space updated successfully!";
            if (updates.items_added) {
              updateMessage = `${updates.items_added} item${updates.items_added > 1 ? "s" : ""} added to collection!`;
            } else if (updates.name) {
              updateMessage = `Space renamed to "${updates.name}"!`;
            } else if (updates.description) {
              updateMessage = "Space description updated!";
            } else if (updates.background_context) {
              updateMessage = "Space guidance updated!";
            }

            dispatch({
              type: "SET_POPUP",
              payload: { text: updateMessage, type: "success" },
            });

            // If we're in the updated space, trigger space reload
            if (currentSpaceId === updatedSpaceId) {
              // Dispatch Redux action to trigger Space.jsx to reload
              dispatch({
                type: "RELOAD_CURRENT_SPACE",
                payload: {
                  spaceId: updatedSpaceId,
                  updates: updates,
                },
              });
            }

            // Clear popup after a delay
            setTimeout(() => {
              dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
            }, 3000);
            break;
          }

          case "refresh_spaces": {
            // Refresh spaces in sidebar
            dispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });

            // Check if sub-collections were created
            const subCollections = actions.data?.sub_collections || [];
            const parentSpaceId = actions.data?.parent_space_id;

            // If sub-collections were created, also refresh the parent space and expand it
            if (subCollections.length > 0 && parentSpaceId) {
              // Dispatch a custom event to refresh the specific parent space
              window.dispatchEvent(new CustomEvent("refreshParentSpace", {
                detail: { parentSpaceId: parentSpaceId },
              }));

              // Also expand the parent space to show the new sub-collections
              window.dispatchEvent(new CustomEvent("expandSpace", {
                detail: { spaceId: parentSpaceId },
              }));
            }

            let successMessage = "Spaces updated successfully!";
            if (subCollections.length > 0) {
              successMessage = `${subCollections.length} sub-collection${subCollections.length > 1 ? "s" : ""} created successfully!`;
            }

            dispatch({
              type: "SET_POPUP",
              payload: { text: successMessage, type: "success" },
            });
            setTimeout(() => {
              dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
            }, 3000);
            break;
          }

          case "filter_data":
            // Filter data
            if (resp.rates?.length > 0) {
              dispatch({ type: "FILTER_RATES", payload: resp.rates });
              if (onResponse) {
                onResponse({ rates: resp.rates, mode: "data_filter" });
              }
            }
            break;

          case "recommendations_updated":
            // Show success notification
            dispatch({
              type: "SET_POPUP",
              payload: { text: "Recommendations updated!", type: "success" },
            });
            setTimeout(() => {
              dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
            }, 3000);
            break;

          case "refresh_chat":
            // Refresh chat messages (e.g., after research completes)
            if (selectedChatId) {
              setTimeout(() => {
                loadChat(selectedChatId);
              }, 500);
            }
            break;

          case "research_started":
            // Research task is already handled above (checking resp.task_id)
            break;

          case "explore":
            // Legacy explore mode - no longer used, but kept for backward compatibility
            break;

          default:
            // Unknown action or 'none'
            break;
        }
      }

      // 12. Handle legacy data filtering (backward compatibility)
      if (resp.rates?.length > 0 && !actions) {
        dispatch({ type: "FILTER_RATES", payload: resp.rates });
        if (onResponse) {
          onResponse({ rates: resp.rates, mode: "data_filter" });
        }
      }

      // 13. Follow-up suggestions: prefer server-provided localized ones
      if (Array.isArray(resp.suggestions) && resp.suggestions.length > 0) {
        setServerSuggestions(
          resp.suggestions
            .filter((s) => typeof s === "string" && s.trim())
            .slice(0, 4)
        );
      } else {
        setServerSuggestions([]);
      }

      setTimeout(() => {
        setShowSuggestions(true);
      }, 1000);
    } catch (err) {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setIsTyping(false);

      // Intentional cancellation (chat switched/closed/unmounted): stay silent
      if (isRequestCancelled(err)) {
        return;
      }

      console.error("Error sending message:", err);

      // Single error report: an in-chat error message with a retry action
      // (no extra popup, so errors aren't double-reported)
      const serverError = err.response?.data?.error;
      const errorMsg = {
        id: makeMessageId(),
        user: "system",
        text: serverError
          ? `I'm sorry, that request failed: ${serverError}`
          : "I'm sorry, I'm having trouble processing that request right now. You can retry, or try rephrasing your question.",
        date: new Date().toLocaleString(),
        isError: true,
        failedCommand: messageText,
      };
      setMessages((prev) => [...prev, errorMsg]);
      setTimeout(() => inputRef.current?.focus(), 0);

      // Show suggestions to help the user
      setTimeout(() => {
        setShowSuggestions(true);
      }, 1000);
    }
  };

  // Retry a failed turn: remove the error bubble (and the failed user
  // bubble, to avoid duplicates), then re-send the original command.
  const handleRetry = (errorMsg) => {
    if (!errorMsg?.failedCommand || isTyping) return;
    setMessages((prev) => {
      const next = prev.filter((m) => m.id !== errorMsg.id);
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].user === "You" && next[i].text === errorMsg.failedCommand) {
          next.splice(i, 1);
          break;
        }
      }
      return next;
    });
    handleSendMessage(errorMsg.failedCommand);
  };

  const starters = CONVERSATION_STARTERS[uiLanguage];
  const emptyStateText = EMPTY_STATE_TEXT[uiLanguage];
  const suggestionChips = serverSuggestions.length > 0 ? serverSuggestions : getSmartSuggestions();

  return (
    <>
      {/* Body overlay to push content when chat is open (desktop only —
          on mobile the chat is full-screen, so this is a no-op) */}
      {isChatOpen && !isMobile && (
        <style>
          {`
            body {
              padding-right: ${isFullscreen ? "0" : ((researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && !isBrowsingMinimized ? "860px" : (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && isBrowsingMinimized ? "508px" : showHistory ? "600px" : "460px")} !important;
              transition: padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            }
            .MuiAppBar-root .MuiToolbar-root {
              padding-right: ${isFullscreen ? "16px" : ((researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && !isBrowsingMinimized ? "876px" : (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && isBrowsingMinimized ? "524px" : showHistory ? "616px" : "476px")} !important;
              transition: padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            }
          `}
        </style>
      )}

      {/* The Chat Widget */}
      {isChatOpen && (
        <Fade in={isChatOpen} timeout={300}>
          <Paper
            elevation={24}
            sx={{
              position: "fixed",
              ...(isFullscreen || isMobile ? {
                // Fullscreen styles (chat is always full-screen on small screens)
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                width: "100vw",
                height: "100vh",
                borderRadius: 0,
                boxShadow: "none",
                border: "none",
              } : {
                // Sidebar styles - extend width when in explore mode
                top: 0,
                right: 0,
                bottom: 0,
                width: (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && !isBrowsingMinimized ? "860px" : (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && isBrowsingMinimized ? "508px" : showHistory ? "600px" : "460px",
                height: "100vh",
                borderRadius: 0,
                boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
                borderLeft: "1px solid rgba(99,102,241,0.12)",
              }),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "#ffffff",
              zIndex: 1000000, // Higher than global plus button (999999)
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                flexShrink: 0,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "1px",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                },
              }}
            >
              {/* Left: identity */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(8px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: "1.2rem", color: "#ffd700" }} />
                  </Box>
                  {/* Online dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 1,
                      right: 1,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4ade80",
                      border: "1.5px solid rgba(118,75,162,0.8)",
                      "@keyframes pulse-dot": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                      },
                      animation: "pulse-dot 2s ease-in-out infinite",
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", letterSpacing: "-0.01em", lineHeight: 1.2, color: "white" }}>
                    AI Agent
                  </Typography>
                  <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.75)", letterSpacing: "0.02em", lineHeight: 1.2 }}>
                    {isTyping ? "Thinking…" : "Online · Ready"}
                  </Typography>
                </Box>
              </Box>

              {/* Right: actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {historicalWebsiteScrolls.length > 0 && (
                  <Tooltip title="Research history">
                    <IconButton
                      onClick={() => {
                        setShowHistoricalWebsites(!showHistoricalWebsites);
                        if (!showHistoricalWebsites) setIsBrowsingMinimized(false);
                      }}
                      sx={{
                        color: "white",
                        padding: "5px",
                        borderRadius: "8px",
                        backgroundColor: showHistoricalWebsites ? "rgba(255,255,255,0.2)" : "transparent",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <LanguageIcon sx={{ fontSize: "1.1rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Chat history">
                  <IconButton
                    onClick={() => setShowHistory(!showHistory)}
                    sx={{
                      color: "white",
                      padding: "5px",
                      borderRadius: "8px",
                      backgroundColor: showHistory ? "rgba(255,255,255,0.2)" : "transparent",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
                {!isMobile && (
                  <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                    <IconButton
                      onClick={() => setIsFullscreen(!isFullscreen)}
                      sx={{
                        color: "white",
                        padding: "5px",
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: "1.1rem" }} /> : <FullscreenIcon sx={{ fontSize: "1.1rem" }} />}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Close">
                  <IconButton
                    onClick={() => setIsChatOpen(false)}
                    sx={{
                      color: "white",
                      padding: "5px",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.15)" },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Chat Body */}
            <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
              {/* Side Panel: List of user chats - Only show when showHistory is true */}
              <ChatHistorySidebar
                open={showHistory}
                isFullscreen={isFullscreen || isMobile}
                userChats={userChats}
                selectedChatId={selectedChatId}
                onSelectChat={handleSelectChat}
                onAddNewChat={handleAddNewChat}
                onConfirmDelete={handleConfirmDelete}
                onConfirmRename={handleConfirmRename}
              />

              {/* Main Chat Area */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexGrow: 1,
                  width: showHistory
                    ? (isFullscreen || isMobile ? "calc(100% - 300px)" : "65%")
                    : "100%",
                  height: "100%",
                  backgroundColor: "#ffffff",
                  transition: "all 0.3s ease",
                }}
              >
                {/* Chat Messages Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    width: (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && !isBrowsingMinimized ? "65%" : "100%",
                    height: "100%",
                    borderRight: (researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && !isBrowsingMinimized ? "1px solid #e5e7eb" : "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {/* Messages Section */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      padding: { xs: "16px 10px", sm: "24px 20px" },
                      overflowY: "auto",
                      backgroundColor: "#ffffff",
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
                    }}
                  >
                    {messages.length > 0 ? (
                      messages.map((msg, index) => (
                        <Fade key={msg.id || `msg-idx-${index}`} in={true} timeout={400}>
                          <Box>
                            <MessageBubble
                              msg={msg}
                              pendingRecommendationSpaces={pendingRecommendationSpaces}
                              onRecommendationResponse={handleRecommendationResponse}
                              onGuidanceResponse={handleGuidanceResponse}
                              onRetry={handleRetry}
                            />
                          </Box>
                        </Fade>
                      ))
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          textAlign: "center",
                          padding: "32px 20px",
                        }}
                      >
                        {/* Glowing icon */}
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: 3,
                            boxShadow: "0 8px 32px rgba(99,102,241,0.3)",
                          }}
                        >
                          <AutoAwesomeIcon sx={{ fontSize: "1.8rem", color: "white" }} />
                        </Box>

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            color: "#0f172a",
                            marginBottom: 1,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {emptyStateText.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#94a3b8", maxWidth: "260px", marginBottom: 3.5, lineHeight: 1.6, fontSize: "0.85rem" }}
                        >
                          {emptyStateText.subtitle}
                        </Typography>

                        {/* Conversation Starters */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", maxWidth: "320px" }}>
                          {starters.map((starter) => (
                            <Box
                              key={starter.text}
                              onClick={() => { setInputValue(starter.text); inputRef.current?.focus(); }}
                              sx={{
                                padding: "10px 16px",
                                backgroundColor: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                cursor: "pointer",
                                fontSize: "0.83rem",
                                color: "#475569",
                                textAlign: uiLanguage === "hebrew" ? "right" : "left",
                                direction: uiLanguage === "hebrew" ? "rtl" : "ltr",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                transition: "all 0.18s ease",
                                "&:hover": {
                                  backgroundColor: "#eef2ff",
                                  borderColor: "#c7d2fe",
                                  color: "#4338ca",
                                  transform: uiLanguage === "hebrew" ? "translateX(-4px)" : "translateX(4px)",
                                },
                              }}
                            >
                              <span style={{ fontSize: "1rem" }}>{starter.icon}</span>
                              {starter.text}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Typing Indicator */}
                    {isTyping && (
                      <Fade in={isTyping} timeout={200}>
                        <Box sx={{ margin: "10px 40px 10px 0", display: "flex", alignItems: "flex-end", gap: 1.5 }}>
                          <Avatar sx={{ width: 28, height: 28, flexShrink: 0, background: "#f1f5f9", border: "1.5px solid #e2e8f0" }}>
                            <AutoAwesomeIcon sx={{ fontSize: "0.95rem", color: "#6366f1" }} />
                          </Avatar>
                          <Box
                            sx={{
                              padding: "12px 18px",
                              borderRadius: "16px 16px 16px 4px",
                              backgroundColor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              "@keyframes bounce-dot": {
                                "0%, 80%, 100%": { transform: "translateY(0)" },
                                "40%": { transform: "translateY(-6px)" },
                              },
                            }}
                          >
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  backgroundColor: "#6366f1",
                                  opacity: 0.7,
                                  animation: "bounce-dot 1.2s ease-in-out infinite",
                                  animationDelay: `${i * 0.18}s`,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Fade>
                    )}

                    {/* Auto-scroll anchor */}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Chat Input */}
                  <Box
                    sx={{
                      padding: { xs: "10px 10px 14px", sm: "12px 16px 16px" },
                      borderTop: "1px solid #f1f5f9",
                      backgroundColor: "#ffffff",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 1,
                        padding: { xs: "8px 10px", sm: "10px 14px" },
                        backgroundColor: "#f8fafc",
                        borderRadius: "16px",
                        border: "1.5px solid #e2e8f0",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                        transition: "all 0.2s ease",
                        "&:focus-within": {
                          borderColor: "#6366f1",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 0 0 3px rgba(99,102,241,0.1)",
                        },
                      }}
                    >
                      <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="standard"
                        placeholder={
                          interactiveSpaceCreation
                            ? (interactiveSpaceCreation.step === "confirm_parent"
                                ? "Choose: 'sub-collection' or 'independent'..."
                                : interactiveSpaceCreation.step === "confirm_name"
                                ? "Type 'yes' to confirm or suggest a new name..."
                                : interactiveSpaceCreation.step === "confirm_subspaces"
                                ? "Type 'yes' to create sub-collections or 'no' to skip..."
                                : interactiveSpaceCreation.step === "confirm_description"
                                ? "Type 'yes' with description or just 'no' to skip..."
                                : interactiveSpaceCreation.step === "add_description"
                                ? "Write a description for your collection..."
                                : "Message AI Assistant...")
                            : "Message AI Assistant..."
                        }
                        value={inputValue}
                        disabled={isTyping}
                        onChange={(e) => {
                          const newValue = e.target.value.slice(0, INPUT_LIMITS.AI_CHAT_MESSAGE);
                          setInputValue(newValue);
                          // Hide suggestions when user starts typing
                          if (newValue.length > 0 && showSuggestions) {
                            setShowSuggestions(false);
                          }
                        }}
                        inputProps={{
                          maxLength: INPUT_LIMITS.AI_CHAT_MESSAGE,
                        }}
                        onFocus={() => {
                          // Show suggestions when focusing if there are recent messages
                          if (messages.length > 0 && !inputValue.trim()) {
                            setTimeout(() => setShowSuggestions(true), 500);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          } else if (e.key === "ArrowUp" && !e.shiftKey && messageHistory.length > 0) {
                            e.preventDefault();
                            const nextIndex = Math.min(messageHistoryIndex + 1, messageHistory.length - 1);
                            setMessageHistoryIndex(nextIndex);
                            setInputValue(messageHistory[nextIndex]);
                          } else if (e.key === "ArrowDown" && !e.shiftKey) {
                            e.preventDefault();
                            if (messageHistoryIndex > 0) {
                              const nextIndex = messageHistoryIndex - 1;
                              setMessageHistoryIndex(nextIndex);
                              setInputValue(messageHistory[nextIndex]);
                            } else if (messageHistoryIndex === 0) {
                              setMessageHistoryIndex(-1);
                              setInputValue("");
                            }
                          } else if (e.key === "Escape") {
                            setShowSuggestions(false);
                          }
                        }}
                        sx={{
                          "& .MuiInput-underline:before": { borderBottom: "none" },
                          "& .MuiInput-underline:after": { borderBottom: "none" },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                          "& .MuiInputBase-input": {
                            fontSize: "0.9rem",
                            lineHeight: 1.5,
                            padding: "3px 0",
                            color: "#0f172a",
                            "&::placeholder": { color: "#94a3b8", opacity: 1 },
                          },
                          "& .MuiInputBase-root": { fontSize: "0.9rem" },
                        }}
                      />

                      <IconButton
                        onClick={() => handleSendMessage()}
                        disabled={!inputValue.trim() || isTyping}
                        aria-label="Send message"
                        sx={{
                          padding: "7px",
                          flexShrink: 0,
                          background: inputValue.trim() && !isTyping
                            ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
                            : "transparent",
                          color: inputValue.trim() && !isTyping ? "white" : "#cbd5e1",
                          borderRadius: "10px",
                          "&:hover": {
                            background: inputValue.trim() && !isTyping
                              ? "linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%)"
                              : "rgba(0,0,0,0.04)",
                            transform: inputValue.trim() && !isTyping ? "scale(1.05)" : "none",
                          },
                          "&:disabled": { background: "transparent", color: "#e2e8f0" },
                          transition: "all 0.2s ease",
                          width: "36px",
                          height: "36px",
                          minWidth: "36px",
                        }}
                      >
                        <SendIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Box>

                    {/* Follow-up suggestions: server-provided chips send on tap,
                        local heuristic chips prefill the input */}
                    {showSuggestions && (
                      <SuggestionChips
                        suggestions={suggestionChips}
                        onSuggestionClick={serverSuggestions.length > 0 ? handleServerSuggestionClick : handleSuggestionClick}
                        show={showSuggestions}
                      />
                    )}

                    {/* Interactive Space Creation Quick Actions */}
                    {interactiveSpaceCreation && (
                      <Fade in={true} timeout={300}>
                        <Box
                          sx={{
                            marginTop: "12px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          {interactiveSpaceCreation.step === "confirm_parent" && (
                            <>
                              <Box
                                onClick={() => handleSendMessage("תת אוסף")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#4CAF50",
                                  border: "1px solid #45a049",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#45a049",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                📁 תת-אוסף
                              </Box>
                              <Box
                                onClick={() => handleSendMessage("אוסף עצמאי חדש")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#2196F3",
                                  border: "1px solid #0b7dda",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#0b7dda",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                🆕 אוסף עצמאי
                              </Box>
                            </>
                          )}
                          {interactiveSpaceCreation.step === "confirm_name" && (
                            <>
                              <Box
                                onClick={() => handleSendMessage("כן זה בסדר")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#4CAF50",
                                  border: "1px solid #45a049",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#45a049",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ✅ כן, זה בסדר
                              </Box>
                              <Box
                                onClick={() => handleSendMessage("לא תודה")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#f44336",
                                  border: "1px solid #da190b",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#da190b",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ❌ לא תודה
                              </Box>
                            </>
                          )}
                          {interactiveSpaceCreation.step === "confirm_subspaces" && (
                            <>
                              <Box
                                onClick={() => handleSendMessage("כן, צור תתי אוספים")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#4CAF50",
                                  border: "1px solid #45a049",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#45a049",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ✅ כן, צור תתי אוספים
                              </Box>
                              <Box
                                onClick={() => handleSendMessage("לא, רק אוסף אחד")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#f44336",
                                  border: "1px solid #da190b",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#da190b",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ❌ לא, רק אוסף אחד
                              </Box>
                            </>
                          )}
                          {interactiveSpaceCreation.step === "confirm_description" && (
                            <>
                              <Box
                                onClick={() => handleSendMessage("כן")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#4CAF50",
                                  border: "1px solid #45a049",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#45a049",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ✅ כן, תוסיף תיאור
                              </Box>
                              <Box
                                onClick={() => handleSendMessage("לא תודה")}
                                sx={{
                                  padding: "6px 12px",
                                  backgroundColor: "#9e9e9e",
                                  border: "1px solid #757575",
                                  borderRadius: "16px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "white",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: "#757575",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                ⏭️ לא תודה
                              </Box>
                            </>
                          )}
                          {interactiveSpaceCreation.step === "add_description" && (
                            <Box
                              onClick={() => handleSendMessage("לא תודה")}
                              sx={{
                                padding: "6px 12px",
                                backgroundColor: "#9e9e9e",
                                border: "1px solid #757575",
                                borderRadius: "16px",
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                color: "white",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: "#757575",
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                              ⏭️ דלג על התיאור
                            </Box>
                          )}
                        </Box>
                      </Fade>
                    )}

                    {/* Input Helper Text */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.67rem",
                        color: "#cbd5e1",
                        marginTop: "7px",
                        display: "block",
                        textAlign: "center",
                        letterSpacing: "0.01em",
                      }}
                    >
                      Enter to send · Shift+Enter for newline · ↑↓ history
                    </Typography>
                  </Box>
                </Box>

                {/* Research Task - Browsing Section */}
                {(researchTaskId || (showHistoricalWebsites && historicalWebsiteScrolls.length > 0)) && (
                  <Box
                    sx={{
                      width: isBrowsingMinimized ? "44px" : "28%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      background: "linear-gradient(180deg, #f5f3ff 0%, #fafafe 100%)",
                      borderLeft: "1px solid #ede9fe",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      overflow: "hidden",
                    }}
                  >
                    {isBrowsingMinimized ? (
                      /* Minimized state */
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "12px 6px",
                          height: "100%",
                          justifyContent: "flex-start",
                          gap: 1,
                        }}
                      >
                        <IconButton
                          onClick={() => setIsBrowsingMinimized(false)}
                          sx={{
                            color: "#7c3aed",
                            padding: "6px",
                            backgroundColor: "rgba(124, 58, 237, 0.08)",
                            borderRadius: "10px",
                            "&:hover": {
                              backgroundColor: "rgba(124, 58, 237, 0.16)",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />
                        </IconButton>
                        {/* Active indicator dot */}
                        {researchTaskId && (researchStatus === "pending" || researchStatus === "in_progress") && (
                          <Box
                            sx={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: "#7c3aed",
                              "@keyframes pulse-dot": {
                                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                "50%": { opacity: 0.5, transform: "scale(0.8)" },
                              },
                              animation: "pulse-dot 1.4s ease-in-out infinite",
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#7c3aed",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            writingMode: "vertical-rl",
                            textOrientation: "mixed",
                            marginTop: "8px",
                            transform: "rotate(180deg)",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          Research
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Browsing Header */}
                        <Box
                          sx={{
                            padding: "12px 14px 10px",
                            borderBottom: "1px solid #ede9fe",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                            {/* Status icon / animated indicator */}
                            <Box
                              sx={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "8px",
                                background: "rgba(255,255,255,0.18)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {(researchTaskId && (researchStatus === "pending" || researchStatus === "in_progress")) ? (
                                <Box sx={{ display: "flex", gap: "3px", alignItems: "center" }}>
                                  {[0, 1, 2].map((i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        width: "4px",
                                        height: "4px",
                                        borderRadius: "50%",
                                        backgroundColor: "white",
                                        "@keyframes bounce-research": {
                                          "0%, 100%": { transform: "translateY(0)" },
                                          "50%": { transform: "translateY(-4px)" },
                                        },
                                        animation: "bounce-research 0.9s ease-in-out infinite",
                                        animationDelay: `${i * 0.15}s`,
                                      }}
                                    />
                                  ))}
                                </Box>
                              ) : researchStatus === "done" ? (
                                <Typography sx={{ fontSize: "0.9rem" }}>✓</Typography>
                              ) : researchStatus === "cancelled" ? (
                                <Typography sx={{ fontSize: "0.9rem" }}>−</Typography>
                              ) : researchStatus === "failed" ? (
                                <Typography sx={{ fontSize: "0.9rem" }}>!</Typography>
                              ) : (
                                <Typography sx={{ fontSize: "0.9rem" }}>📚</Typography>
                              )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  fontSize: "0.8rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  lineHeight: 1.2,
                                }}
                              >
                                {showHistoricalWebsites && !researchTaskId
                                  ? "Research History"
                                  : researchQuery
                                  ? researchQuery
                                  : "Web Research"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "rgba(255,255,255,0.72)",
                                  fontSize: "0.65rem",
                                  fontWeight: 500,
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {showHistoricalWebsites && !researchTaskId
                                  ? `${historicalWebsiteScrolls.length} site${historicalWebsiteScrolls.length !== 1 ? "s" : ""} analyzed`
                                  : researchStatus === "pending" || researchStatus === "in_progress"
                                  ? `${websiteScrolls.length > 0 ? `${websiteScrolls.length} found · ` : ""}Analyzing…`
                                  : researchStatus === "done"
                                  ? `${websiteScrolls.length} site${websiteScrolls.length !== 1 ? "s" : ""} analyzed`
                                  : researchStatus === "cancelled"
                                  ? "Research cancelled"
                                  : researchStatus === "failed"
                                  ? "Research failed"
                                  : "Complete"}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {/* Cancel button - only when active */}
                            {researchTaskId && (researchStatus === "pending" || researchStatus === "in_progress") && (
                              <Tooltip title="Cancel research" placement="bottom">
                                <IconButton
                                  onClick={cancelResearch}
                                  sx={{
                                    color: "rgba(255,255,255,0.8)",
                                    padding: "4px",
                                    borderRadius: "8px",
                                    "&:hover": {
                                      backgroundColor: "rgba(239,68,68,0.25)",
                                      color: "#fca5a5",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                  size="small"
                                >
                                  <CancelIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Minimize button */}
                            <IconButton
                              onClick={() => setIsBrowsingMinimized(true)}
                              sx={{
                                color: "rgba(255,255,255,0.8)",
                                padding: "4px",
                                borderRadius: "8px",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.15)",
                                  color: "white",
                                },
                                transition: "all 0.2s ease",
                              }}
                              size="small"
                            >
                              <ChevronRightIcon sx={{ fontSize: "1.1rem" }} />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Browsing Results */}
                        <Box
                          sx={{
                            flexGrow: 1,
                            overflowY: "auto",
                            padding: "10px",
                            position: "relative",
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": { background: "#c4b5fd", borderRadius: "4px" },
                          }}
                        >
                          {/* Loading card - sticky at top while processing */}
                          {(researchStatus === "pending" || researchStatus === "in_progress") && !showHistoricalWebsites && (
                            <Box
                              sx={{
                                padding: "14px 12px",
                                background: "white",
                                borderRadius: "12px",
                                border: "1px solid #ede9fe",
                                marginBottom: websiteScrolls.length > 0 ? "10px" : 0,
                                position: websiteScrolls.length > 0 ? "sticky" : "static",
                                top: 0,
                                zIndex: 10,
                                boxShadow: "0 2px 12px rgba(102,126,234,0.10)",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: "10px" }}>
                                <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                  {[0, 1, 2].map((i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        backgroundColor: "#7c3aed",
                                        "@keyframes bounce-load": {
                                          "0%, 100%": { transform: "translateY(0)", opacity: 0.6 },
                                          "50%": { transform: "translateY(-5px)", opacity: 1 },
                                        },
                                        animation: "bounce-load 0.9s ease-in-out infinite",
                                        animationDelay: `${i * 0.18}s`,
                                      }}
                                    />
                                  ))}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#4c1d95",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  Researching websites…
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#7c3aed",
                                  fontSize: "0.68rem",
                                  display: "block",
                                  marginBottom: "10px",
                                  opacity: 0.75,
                                }}
                              >
                                Browsing and analyzing sources to build your answer
                              </Typography>
                              <Box
                                onClick={cancelResearch}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "4px 10px",
                                  background: "rgba(239,68,68,0.07)",
                                  border: "1px solid rgba(239,68,68,0.25)",
                                  borderRadius: "20px",
                                  cursor: "pointer",
                                  color: "#dc2626",
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    background: "rgba(239,68,68,0.14)",
                                    borderColor: "rgba(239,68,68,0.5)",
                                  },
                                }}
                              >
                                <CancelIcon sx={{ fontSize: "0.75rem" }} />
                                Stop
                              </Box>
                            </Box>
                          )}

                          {/* Cancelled status */}
                          {researchStatus === "cancelled" && !showHistoricalWebsites && (
                            <Box
                              sx={{
                                padding: "12px",
                                background: "rgba(239,68,68,0.05)",
                                borderRadius: "12px",
                                border: "1px solid rgba(239,68,68,0.18)",
                                marginBottom: websiteScrolls.length > 0 ? "10px" : 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: "#dc2626",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" sx={{ color: "#991b1b", fontSize: "0.75rem", fontWeight: 500 }}>
                                Research stopped
                              </Typography>
                            </Box>
                          )}

                          {/* Failed status */}
                          {researchStatus === "failed" && !showHistoricalWebsites && (
                            <Box
                              sx={{
                                padding: "12px",
                                background: "rgba(239,68,68,0.05)",
                                borderRadius: "12px",
                                border: "1px solid rgba(239,68,68,0.18)",
                                marginBottom: websiteScrolls.length > 0 ? "10px" : 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: "#dc2626",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" sx={{ color: "#991b1b", fontSize: "0.75rem", fontWeight: 500 }}>
                                Research failed — please try again
                              </Typography>
                            </Box>
                          )}

                          {/* Results list */}
                          {(() => {
                            const displayScrolls = (showHistoricalWebsites && !researchTaskId)
                              ? historicalWebsiteScrolls
                              : websiteScrolls;

                            return displayScrolls.length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {displayScrolls.map((scroll, index) => (
                                  <Card
                                    key={scroll.url || index}
                                    sx={{
                                      border: "1px solid #ede9fe",
                                      borderRadius: "12px",
                                      boxShadow: "none",
                                      transition: "all 0.2s ease",
                                      overflow: "hidden",
                                      cursor: "pointer",
                                      "&:hover": {
                                        boxShadow: "0 4px 16px rgba(102,126,234,0.14)",
                                        transform: "translateY(-2px)",
                                        borderColor: "#c4b5fd",
                                      },
                                    }}
                                    onClick={() => window.open(scroll.url, "_blank")}
                                  >
                                    {/* Card image */}
                                    {scroll.image_url && (
                                      <Box
                                        sx={{
                                          overflow: "hidden",
                                          height: "90px",
                                          backgroundColor: "#f1f0fe",
                                        }}
                                      >
                                        <img
                                          src={scroll.image_url}
                                          alt={scroll.title || "Website"}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      </Box>
                                    )}

                                    <CardContent sx={{ padding: "10px 12px 10px !important" }}>
                                      {/* Title */}
                                      <Tooltip title={scroll.title} placement="top">
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            color: "#1e1b4b",
                                            fontSize: "0.78rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            lineHeight: 1.3,
                                            marginBottom: "3px",
                                            "&:hover": { color: "#7c3aed" },
                                          }}
                                        >
                                          {scroll.title}
                                        </Typography>
                                      </Tooltip>

                                      {/* URL */}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#7c3aed",
                                          fontSize: "0.67rem",
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          marginBottom: "4px",
                                          opacity: 0.7,
                                        }}
                                      >
                                        {scroll.url}
                                      </Typography>

                                      {/* Stars */}
                                      {scroll.stars && scroll.stars > 0 && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginBottom: "4px" }}>
                                          <Rating
                                            value={scroll.stars}
                                            readOnly
                                            size="small"
                                            emptyIcon={<StarIcon style={{ opacity: 0.35 }} fontSize="inherit" />}
                                            sx={{ color: "#f59e0b", fontSize: "13px" }}
                                          />
                                          {scroll.stars_given_by_ai === true && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: "0.6rem",
                                                color: "#7c3aed",
                                                fontWeight: 500,
                                                padding: "1px 5px",
                                                backgroundColor: "rgba(124,58,237,0.08)",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              AI est.
                                            </Typography>
                                          )}
                                        </Box>
                                      )}

                                      {/* AI summary */}
                                      {scroll.ai_short_summary && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "#64748b",
                                            fontSize: "0.69rem",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {scroll.ai_short_summary}
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            ) : (showHistoricalWebsites && !researchTaskId) ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  padding: "32px 16px",
                                  textAlign: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography sx={{ fontSize: "1.8rem", opacity: 0.4 }}>📭</Typography>
                                <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                                  No websites in history
                                </Typography>
                              </Box>
                            ) : researchStatus === "done" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  padding: "32px 16px",
                                  textAlign: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography sx={{ fontSize: "1.8rem", opacity: 0.4 }}>🔎</Typography>
                                <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                                  No websites found
                                </Typography>
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Floating Button to open/close chat (hide on mobile) */}
      {!isChatOpen && !isMobile && (
        <Grow in={!isChatOpen} timeout={500}>
          <Fab
            onClick={() => setIsChatOpen(true)}
            sx={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              width: "56px",
              height: "56px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                transform: "scale(1.1) rotate(5deg)",
                boxShadow: "0 8px 28px rgba(102,126,234,0.55)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: "2.5px solid white",
              zIndex: 999998,
              "@keyframes fab-pulse": {
                "0%, 100%": { boxShadow: "0 6px 20px rgba(102,126,234,0.4)" },
                "50%": { boxShadow: "0 6px 28px rgba(118,75,162,0.65)" },
              },
              animation: "fab-pulse 2.5s ease-in-out infinite",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
              <AutoAwesomeIcon sx={{ fontSize: "1.4rem", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: "0.58rem", letterSpacing: "0.05em", color: "white", lineHeight: 1, textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}
              >
                AI
              </Typography>
            </Box>
          </Fab>
        </Grow>
      )}
    </>
  );
};

export default ChatComponent;
